// 📄 pages/Admin/components/PlantillaGlobalFormModal.tsx
import React, { useEffect, useRef, useState } from 'react';
import { X, Save, RefreshCw, UploadCloud, AlertTriangle } from 'lucide-react';
import { existePlantilla, guardarPlantilla, guardarRestricciones, PlantillaGlobal, PlantillaMetadatos } from '../../../services/plantillaReferenciaService.ts';
import { construirIdPlantilla } from '../../../utils/plantillaIdUtils.ts';
import { parseXmlString, RestriccionTag } from '../../../utils/xmlStructureUtils.ts';
import RestriccionesTagEditor from '../../../components/shared/RestriccionesTagEditor.tsx';
import PlantillaMetadatosForm from '../../../components/shared/PlantillaMetadatosForm.tsx';

interface PlantillaGlobalFormModalProps {
  // Presente = editar (reemplazar archivo de un tipo ya existente, el
  // tipoDocumento no se puede cambiar porque es el id del documento);
  // ausente = crear un tipo nuevo.
  plantilla?: PlantillaGlobal | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function PlantillaGlobalFormModal({ plantilla, onClose, onSaved }: PlantillaGlobalFormModalProps) {
  const editando = !!plantilla;
  // Nombre legible que escribe el admin — en modo alta es la base a partir
  // de la cual se deriva el id de Firestore (ver tipoDocumentoCompuesto más
  // abajo); en modo edición es solo de lectura (el input sigue
  // deshabilitado), precargado con el nombre guardado o, si la plantilla es
  // de antes de que existiera este campo, con el tipoDocumento crudo como
  // fallback — así el admin ve algo legible en vez del id con guiones.
  const [nombre, setNombre] = useState(plantilla?.nombre || plantilla?.tipoDocumento || '');
  const [nombreArchivo, setNombreArchivo] = useState(plantilla?.nombreArchivo || '');
  const [contenidoXML, setContenidoXML] = useState(plantilla?.contenidoXML || '');
  const [restricciones, setRestricciones] = useState<RestriccionTag[]>(plantilla?.restricciones || []);
  const metadatosIniciales: PlantillaMetadatos = {
    documentType: plantilla?.documentType || '',
    documentStage: plantilla?.documentStage || '',
    documentStandard: plantilla?.documentStandard || '',
    sistemaOrigen: plantilla?.sistemaOrigen || '',
    sistemaDestino: plantilla?.sistemaDestino || '',
    descripcion: plantilla?.descripcion || ''
  };
  const [metadatos, setMetadatos] = useState<PlantillaMetadatos>(metadatosIniciales);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Id real de Firestore: en alta se deriva de nombre + Document Stage (ver
  // plantillaIdUtils.ts); en edición queda fijo, nunca se recalcula — el id
  // se fija en la creación y no cambia después.
  const tipoDocumentoCompuesto = construirIdPlantilla(nombre, metadatos.documentStage);

  const [existeExistente, setExisteExistente] = useState<boolean | null>(null);
  const [verificandoExistencia, setVerificandoExistencia] = useState(false);

  useEffect(() => {
    if (editando) { setExisteExistente(null); return; }
    if (!tipoDocumentoCompuesto) { setExisteExistente(null); return; }
    setVerificandoExistencia(true);
    existePlantilla(tipoDocumentoCompuesto, 'global')
      .then(setExisteExistente)
      .catch((err) => {
        console.error('Error verificando plantilla existente:', err);
        setExisteExistente(null);
      })
      .finally(() => setVerificandoExistencia(false));
  }, [editando, tipoDocumentoCompuesto]);

  const handleArchivo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const texto = reader.result as string;
      try {
        parseXmlString(texto);
      } catch (err: any) {
        setError(err.message || 'El archivo subido no es un XML válido.');
        return;
      }
      setError(null);
      setNombreArchivo(file.name);
      setContenidoXML(texto);
    };
    reader.readAsText(file);
  };

  // Si ni el archivo ni los metadatos cambiaron, guardar solo las
  // restricciones (partial update) evita tener que volver a mandar el XML
  // completo — ver comentario en guardarPlantilla (plantillaReferenciaService.ts)
  // sobre por qué un guardado completo SIEMPRE debe incluir el snapshot
  // vigente de restricciones, para no perderlas en silencio. Los metadatos
  // solo se persisten por esa vía completa (guardarRestricciones no los
  // toca), así que si el admin edita SOLO metadatos también hace falta el
  // guardado completo. El id (tipoDocumento) sale de tipoDocumentoCompuesto
  // solo en alta — en edición se mantiene el de la plantilla existente,
  // nunca se recalcula.
  const handleGuardar = async () => {
    setError(null);
    if (!nombre.trim()) { setError('Escribe un nombre para el tipo de documento.'); return; }
    if (!contenidoXML.trim()) { setError('Sube un archivo XML de referencia.'); return; }
    if (!editando && existeExistente) {
      setError('Ya existe una plantilla con este nombre y Document Stage. Cambia el nombre o selecciona un stage diferente.');
      return;
    }

    setGuardando(true);
    try {
      const idAGuardar = editando ? plantilla!.tipoDocumento : tipoDocumentoCompuesto;
      const archivoCambio = !editando || contenidoXML !== (plantilla?.contenidoXML || '');
      const metadatosCambiaron = JSON.stringify(metadatos) !== JSON.stringify(metadatosIniciales);
      if (archivoCambio || metadatosCambiaron) {
        await guardarPlantilla(idAGuardar, nombreArchivo, contenidoXML, 'global', undefined, nombre.trim(), restricciones, metadatos);
      } else {
        await guardarRestricciones(idAGuardar, restricciones, 'global');
      }
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Error al guardar la plantilla.');
    } finally {
      setGuardando(false);
    }
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      {/* max-h-[90vh] + flex-col con header/footer flex-shrink-0 y el body
          flex-1 overflow-y-auto: la sección de restricciones puede crecer
          bastante (lista de tags), header y footer siempre quedan visibles. */}
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-4xl shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
          <h3 className="text-lg font-medium text-white">{editando ? 'Editar plantilla global' : 'Nueva plantilla global'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

          <div>
            <label className={labelClass}>Tipo de documento</label>
            <input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              disabled={editando}
              placeholder="ej. Orden de Compra de Buying"
              className={`${inputClass} disabled:opacity-50`}
            />
            {editando ? (
              <p className="text-[11px] text-gray-500 mt-1">El tipo de documento no se puede cambiar — crea uno nuevo si necesitas otro.</p>
            ) : (
              <>
                {tipoDocumentoCompuesto && (
                  <p className="text-[11px] text-gray-500 mt-1">
                    ID de Firestore: <span className="font-mono text-gray-400">{tipoDocumentoCompuesto}</span>
                  </p>
                )}
                {verificandoExistencia && (
                  <p className="text-[11px] text-gray-500 mt-1 flex items-center gap-1.5">
                    <RefreshCw className="w-3 h-3 animate-spin" /> Verificando disponibilidad...
                  </p>
                )}
                {existeExistente && !verificandoExistencia && (
                  <p className="text-[11px] text-red-400 mt-1 flex items-start gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    Ya existe una plantilla con este nombre y Document Stage. Cambia el nombre o selecciona un stage diferente.
                  </p>
                )}
              </>
            )}
          </div>

          <div>
            <label className={labelClass}>Archivo XML de referencia</label>
            <div
              onClick={() => inputRef.current?.click()}
              className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-white/15 hover:border-indigo-400 rounded-xl p-6 text-center cursor-pointer transition-all"
            >
              <UploadCloud className="w-6 h-6 text-gray-500" />
              <p className="text-xs text-gray-400">Haz click para seleccionar un archivo .xml</p>
              <input ref={inputRef} type="file" accept=".xml" onChange={handleArchivo} className="hidden" />
            </div>
            {nombreArchivo && (
              <p className="text-xs text-gray-400 mt-2">
                {editando ? 'Archivo actual: ' : 'Archivo seleccionado: '}
                <span className="text-gray-200">{nombreArchivo}</span>
              </p>
            )}
          </div>

          <div className="pt-2 border-t border-white/10">
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Metadatos de trazabilidad (opcional)</h4>
            <PlantillaMetadatosForm valores={metadatos} onChange={setMetadatos} />
          </div>

          <div className="pt-2 border-t border-white/10">
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Restricciones por campo (opcional)</h4>
            <RestriccionesTagEditor contenidoXML={contenidoXML} restricciones={restricciones} onChange={setRestricciones} />
          </div>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-white/10 flex-shrink-0">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all">Cancelar</button>
          <button
            type="button"
            onClick={handleGuardar}
            disabled={guardando || (!editando && !!existeExistente)}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-semibold text-[#fff] transition-all"
          >
            {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
